<template>
  <div class="bg-gray-25">
    <div class="max-w-7xl mx-auto w-full flex flex-col md:flex-row gap-8 py-8 px-4 ">
      <aside class="w-full md:w-[280px] flex-shrink-0 flex flex-col gap-6">
        <div class="w-full relative shadow-sm rounded-lg border border-gray-200 bg-white">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LdSearchIcon></LdSearchIcon>
          </div>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search"
            class="block w-full pl-10 pr-3 py-3 border-none rounded-lg focus:ring-teal-800 focus:border-teal-800 sm:text-sm"
          />
        </div>

        <FilterSection
          title="Living Wage or Living Income"
          title-key="filters.focus_area"
          :options="focusAreaOptions"
          v-model="filters.focusAreas"
        />
        <FilterSection
          title="Organisation Type"
          title-key="filters.organisation_type"
          :options="uniqueTypes"
          v-model="filters.types"
        />
        <FilterSection
          title="Location"
          title-key="filters.location"
          :options="uniqueLocations"
          v-model="filters.locations"
        />
        <FilterSection
          title="Sector"
          title-key="filters.sector"
          :options="uniqueSectors"
          v-model="filters.sectors"
        />
        <FilterSection
          title="Aspects of Living Wage"
          title-key="filters.aspects"
          :options="uniqueAspects"
          v-model="filters.aspects"
        />
      </aside>

      <main class="flex-1 flex flex-col gap-6">
        <div class="flex flex-wrap items-center gap-4 py-2">
          <h2 class="text-teal-900 text-lg font-bold">
            {{ filteredOrganisations.length }} <Translation name="main.results" default="Results" />
          </h2>
          <div class="w-px h-6 bg-gray-300"></div>

          <button
            v-for="filter in activeFilters"
            :key="filter"
            @click="removeFilter(filter)"
            class="px-4 py-1.5 bg-emerald-50 text-teal-900 text-sm font-semibold rounded-full hover:bg-emerald-100 flex items-center gap-2 transition-colors"
          >
            {{ filter }} <span class="text-teal-700">&times;</span>
          </button>

          <button
            v-if="activeFilters.length > 0 || searchQuery"
            @click="clearAllFilters"
            class="text-teal-800 text-sm font-semibold hover:underline ml-2"
          >
            <Translation name="main.clear_filters" default="Clear All Filters" />
          </button>
        </div>

        <div v-if="paginatedOrganisations.length > 0" class="flex flex-col gap-6">
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <OrganisationCard
              v-for="org in paginatedOrganisations"
              :key="org.id"
              :organisation="org"
            />
          </div>

          <Pagination
            v-if="totalPages > 1"
            :total-pages="totalPages"
            v-model:current-page="currentPage"
          />
        </div>

        <div
          v-else
          class="w-full py-20 bg-white rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center"
        >
          <div v-html="noOrganistaioans"></div>
          <h3 class="text-lg font-medium text-gray-900">
            <Translation name="main.no_results_title" default="No organisations found" />
          </h3>
          <p class="text-gray-500">
            <Translation name="main.no_results_desc" default="Try adjusting your filters or search query." />
          </p>
        </div>
      </main>
    </div>
    <OrganisationModal
      v-if="deepLinkedOrg"
      :organisation="deepLinkedOrg"
      :is-open="isDeepLinkModalOpen"
      @close="closeDeepLinkModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import FilterSection from "./FilterSection.vue";
import OrganisationCard from "./OrganisationCard.vue";
import Pagination from "./Pagination.vue";
import OrganisationModal from "./OrganisationModal.vue";
import LdSearchIcon from "../icons/LdSearchIcon.vue";

const props = defineProps({
  initialOrganisations: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const deepLinkedOrg = ref(null);
const isDeepLinkModalOpen = ref(false);

onMounted(() => {
  const params = new URLSearchParams(window.location.search);
  const orgId = params.get('org_id');

  if (orgId) {
    const foundOrg = props.initialOrganisations.find(
      (org) => org.id.toString() === orgId
    );
    
    if (foundOrg) {
      deepLinkedOrg.value = foundOrg;
      isDeepLinkModalOpen.value = true;
    }
  }
});

const closeDeepLinkModal = () => {
  isDeepLinkModalOpen.value = false;
  
  const url = new URL(window.location);
  url.searchParams.delete('org_id');
  window.history.replaceState({}, '', url);
};

const searchQuery = ref("");
const filters = ref({
  focusAreas: [],
  types: [],
  locations: [],
  sectors: [],
  aspects: [],
});

const focusAreaOptions = ["Living Wage", "Living Income"];
const focusAreaValues = {
  "Living Wage": "living_wage",
  "Living Income": "living_income",
};

const currentPage = ref(1);
const itemsPerPage = 9;

const uniqueTypes = computed(() => {
  const items = props.initialOrganisations.map((org) => org.organisation_type).filter(Boolean);
  return [...new Set(items)].sort();
});

const uniqueLocations = computed(() => {
  const items = props.initialOrganisations.flatMap((org) => org.countries || []).filter(Boolean);
  return ["Global", ...[...new Set(items)].filter((item) => item !== "Global").sort()];
});

const uniqueAspects = computed(() => {
  const items = props.initialOrganisations.flatMap((org) => org.aspects || []).filter(Boolean);
  return [...new Set(items)].sort();
});

const uniqueSectors = computed(() => {
  const items = props.initialOrganisations.flatMap((org) => org.sectors || []).filter(Boolean);
  return [...new Set(items)].sort();
});

const activeFilters = computed(() => {
  return [
    ...filters.value.focusAreas,
    ...filters.value.types,
    ...filters.value.locations,
    ...filters.value.sectors,
    ...filters.value.aspects,
  ];
});

const removeFilter = (filterName) => {
  filters.value.focusAreas = filters.value.focusAreas.filter((f) => f !== filterName);
  filters.value.sectors = filters.value.sectors.filter((f) => f !== filterName);
  if (filters.value.types.includes(filterName)) {
    filters.value.types = filters.value.types.filter((f) => f !== filterName);
  }
  if (filters.value.locations.includes(filterName)) {
    filters.value.locations = filters.value.locations.filter(
      (f) => f !== filterName
    );
  }
  if (filters.value.aspects.includes(filterName)) {
    filters.value.aspects = filters.value.aspects.filter(
      (f) => f !== filterName
    );
  }
};

const clearAllFilters = () => {
  searchQuery.value = "";
  filters.value.focusAreas = [];
  filters.value.types = [];
  filters.value.locations = [];
  filters.value.sectors = [];
  filters.value.aspects = [];
};

const filteredOrganisations = computed(() => {
  return props.initialOrganisations.filter((org) => {
    const matchesSearch =
      !searchQuery.value ||
      org.name?.toLowerCase().includes(searchQuery.value.toLowerCase());

    const matchesType =
      filters.value.types.length === 0 ||
      filters.value.types.includes(org.organisation_type);

    const matchesFocusArea =
      filters.value.focusAreas.length === 0 ||
      org.focus_area === "both" ||
      filters.value.focusAreas.some((area) => focusAreaValues[area] === org.focus_area);

    const matchesLocation =
      filters.value.locations.length === 0 ||
      (filters.value.locations.includes("Global") && org.regional_scope === "global") ||
      org.countries?.some((c) => filters.value.locations.includes(c));

    const matchesSector =
      filters.value.sectors.length === 0 ||
      org.sectors?.some((sector) => filters.value.sectors.includes(sector));

    const matchesAspect =
      filters.value.aspects.length === 0 ||
      org.aspects?.some((a) => filters.value.aspects.includes(a));

    return matchesSearch && matchesType && matchesFocusArea && matchesLocation && matchesSector && matchesAspect;
  });
});

const totalPages = computed(() =>
  Math.ceil(filteredOrganisations.value.length / itemsPerPage)
);

const paginatedOrganisations = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredOrganisations.value.slice(start, end);
});

watch(
  [searchQuery, filters],
  () => {
    currentPage.value = 1;
  },
  { deep: true }
);
</script>
